import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Smartphone, Download, Share2 } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }

  interface Navigator {
    standalone?: boolean;
  }
}

const STORAGE_KEYS = {
  PROMPT_DISMISSED: 'pwa-prompt-dismissed',
  LAST_PROMPT_TIME: 'pwa-last-prompt-time',
} as const;

const ONE_WEEK = 7 * 24 * 60 * 60 * 1000;

const isIOSDevice = (): boolean => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent) && !('standalone' in window.navigator);
};

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(
    null
  );
  const [showIOSPrompt, setShowIOSPrompt] = useState<boolean>(false);
  const [isInstalled, setIsInstalled] = useState<boolean>(false);

  const shouldShowPrompt = (): boolean => {
    const dismissed = localStorage.getItem(STORAGE_KEYS.PROMPT_DISMISSED);
    const lastPromptTime = localStorage.getItem(STORAGE_KEYS.LAST_PROMPT_TIME);

    if (!dismissed) return true;

    if (lastPromptTime) {
      const timeSinceLastPrompt = Date.now() - parseInt(lastPromptTime, 10);
      return timeSinceLastPrompt > ONE_WEEK;
    }

    return false;
  };

  const dismissPrompt = (): void => {
    localStorage.setItem(STORAGE_KEYS.PROMPT_DISMISSED, 'true');
    localStorage.setItem(STORAGE_KEYS.LAST_PROMPT_TIME, Date.now().toString());
    setDeferredPrompt(null);
    setShowIOSPrompt(false);
  };

  useEffect(() => {
    const checkInstallation = (): void => {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return;
      }
    };

    checkInstallation();

    if (shouldShowPrompt()) {
      const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent): void => {
        e.preventDefault();
        setDeferredPrompt(e);
      };

      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

      if (isIOSDevice()) {
        setShowIOSPrompt(true);
      }

      return () => {
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }
  }, []);

  const handleAndroidInstall = async (): Promise<void> => {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        setIsInstalled(true);
      } else {
        dismissPrompt();
      }
      setDeferredPrompt(null);
    } catch (error) {
      console.error('Error installing PWA:', error);
      dismissPrompt();
    }
  };

  if (isInstalled || !shouldShowPrompt()) return null;

  return (
    <div className="fixed bottom-4 left-0 right-0 mx-auto px-4 z-50 max-w-md">
      {deferredPrompt && (
        <Alert className="bg-primary backdrop-blur supports-[backdrop-filter]:bg-primary/75 text-primary-foreground shadow-lg border-0 overflow-hidden">
          <div className="flex items-start gap-3">
            <div className="bg-primary-foreground/10 p-2 rounded-md">
              <Smartphone className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <AlertTitle className="text-sm font-semibold mb-1">Get our app</AlertTitle>
              <AlertDescription className="text-sm text-primary-foreground/90">
                Install for a better experience
              </AlertDescription>
            </div>
            <div className="flex items-center gap-2 ml-2">
              <Button
                variant="secondary"
                size="sm"
                className="text-xs px-3 h-8 shadow-sm hover:shadow"
                onClick={handleAndroidInstall}
              >
                <Download className="mr-1 h-3 w-3" />
                Install
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={dismissPrompt}
                className="text-xs h-8 px-2 hover:bg-primary-foreground/10"
              >
                ✕
              </Button>
            </div>
          </div>
        </Alert>
      )}

      {showIOSPrompt && (
        <Alert className="bg-primary/95 backdrop-blur supports-[backdrop-filter]:bg-primary/75 text-primary-foreground shadow-lg border-0">
          <div className="flex items-start gap-3">
            <div className="bg-primary-foreground/10 p-2 rounded-md">
              <Smartphone className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <AlertTitle className="text-sm font-semibold mb-1">Get our app</AlertTitle>
              <AlertDescription className="text-sm text-primary-foreground/90">
                <ol className="space-y-1 mt-1">
                  <li className="flex items-center gap-1">
                    1. Tap <Share2 className="inline h-3 w-3" /> Share
                  </li>
                  <li>2. Choose "Add to Home Screen"</li>
                </ol>
              </AlertDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={dismissPrompt}
              className="text-xs h-8 px-2 hover:bg-primary-foreground/10"
            >
              ✕
            </Button>
          </div>
        </Alert>
      )}
    </div>
  );
};

export default InstallPrompt;
